import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, FileText, Loader2, Mail, Pencil } from 'lucide-react'
import {
  Button,
  Chip,
  PageHeaderBand,
  QueryErrorState,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { isReadingPdf, useArchiveSop, usePublishSop, useSop, useSopTemplate } from '../api/queries'
import type { SopStatus } from '../api/types'
import { sopDownloadUrl } from '../utils/sop-url'
import { formatSopDate } from '../utils/sop-html'
import { DocumentControl, SopDocumentView } from './SopDocumentView'
import { SopEmailDrawer } from './SopEmailDrawer'
import { SopStatusChip } from './SopStatusChip'

export interface SopReaderProps {
  sopId: string
  /** Where "back" goes: the list this SOP was opened from. */
  backTo: string
  backLabel: string
  /** SOP Management: Edit / Publish / Archive, empty template sections and
   *  the gaps banner. The library never passes this. */
  canAct?: boolean
  /** Path of the editor for this SOP (SOP Management only). */
  editTo?: string
}

/** A full page for one SOP: the document, its header, and its actions. */
export function SopReader({ sopId, backTo, backLabel, canAct = false, editTo }: SopReaderProps) {
  const navigate = useNavigate()
  const { data: sop, isLoading, isError, error, refetch } = useSop(sopId)
  const { data: template } = useSopTemplate()
  const publish = usePublishSop()
  const archive = useArchiveSop()
  const [emailOpen, setEmailOpen] = useState(false)

  const back = (
    <Button variant="ghost" size="sm" onClick={() => navigate({ to: backTo })}>
      <ArrowLeft size={16} /> {backLabel}
    </Button>
  )

  if (isError || (!isLoading && !sop)) {
    return (
      <div className="flex h-full flex-col">
        <Toolbar><ToolbarLeft>{back}</ToolbarLeft></Toolbar>
        <div className="p-6">
          <QueryErrorState error={error} onRetry={() => void refetch()} subject="this SOP" />
        </div>
      </div>
    )
  }
  if (!sop) {
    return (
      <div className="flex h-full flex-col">
        <Toolbar><ToolbarLeft>{back}</ToolbarLeft></Toolbar>
        <div className="p-6 text-md text-secondary">Loading…</div>
      </div>
    )
  }

  const updated = formatSopDate(sop.updated_at)

  return (
    <div data-density="comfortable" data-surface="form" className="flex h-full flex-col">
      <PageHeaderBand
        title={sop.title}
        description={`${sop.category}${sop.version ? ` · Version ${sop.version}` : ''}${updated ? ` · Updated ${updated}` : ''}`}
      >
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Chip variant="neutral">{sop.category}</Chip>
          {canAct ? <SopStatusChip status={sop.status as SopStatus} /> : null}
          {canAct && sop.gaps.length ? (
            <Chip variant="warning" dot title={sop.gaps.join(', ')}>
              {sop.gaps.length} to complete
            </Chip>
          ) : null}
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>{back}</ToolbarLeft>
        <ToolbarRight>
          <Button variant="ghost" size="sm" onClick={() => setEmailOpen(true)}>
            <Mail size={16} /> Email
          </Button>
          {sop.has_pdf ? (
            <Button variant="ghost" size="sm" onClick={() => window.open(sopDownloadUrl(sop.id), '_blank', 'noopener')}>
              <FileText size={16} /> Original PDF
            </Button>
          ) : null}
          {canAct && editTo ? (
            <Button variant="secondary" size="sm" onClick={() => navigate({ to: editTo })}>
              <Pencil size={16} /> Edit
            </Button>
          ) : null}
          {canAct && sop.status !== 'published' ? (
            <Button
              variant="primary"
              size="sm"
              loading={publish.isPending}
              onClick={() =>
                publish.mutate(sop.id, {
                  onSuccess: () => toast.success('SOP published'),
                  onError: (err) => toast.danger(`Publish failed${err instanceof Error ? `: ${err.message}` : ''}`),
                })
              }
            >
              Publish
            </Button>
          ) : null}
          {canAct && sop.status !== 'archived' ? (
            <Button
              variant="danger"
              size="sm"
              loading={archive.isPending}
              onClick={() =>
                archive.mutate(sop.id, {
                  onSuccess: () => toast.success('SOP archived'),
                  onError: (err) => toast.danger(`Archive failed${err instanceof Error ? `: ${err.message}` : ''}`),
                })
              }
            >
              Archive
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto bg-canvas px-6 py-6">
        {canAct && sop.gaps.length ? (
          <div role="status" className="mx-auto mb-6 flex max-w-[1180px] flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-warning-border bg-warning-bg px-5 py-3 text-md text-warning-text">
            <span className="font-semibold">Missing from the SOP template:</span>
            <span>{sop.gaps.join(', ')}</span>
            {editTo ? (
              <Button variant="secondary" size="sm" className="ml-auto" onClick={() => navigate({ to: editTo })}>
                Complete it
              </Button>
            ) : null}
          </div>
        ) : null}

        {canAct && isReadingPdf(sop) ? (
          <div role="status" className="mx-auto mb-6 flex max-w-[1180px] items-center gap-3 rounded-lg border border-info-border bg-info-bg px-5 py-3 text-md text-info-text">
            <Loader2 size={18} className="animate-spin" aria-hidden="true" />
            Reading the PDF into the template… the sections fill in on their own.
          </div>
        ) : null}

        {/* A PDF not read into the template yet is shown as the PDF itself. */}
        {sop.has_pdf && !sop.is_template ? (
          <div className="mx-auto flex h-full max-w-[1180px] flex-col overflow-hidden rounded-lg border border-border-default bg-surface shadow-sm">
            <DocumentControl sop={sop} highlightMissing={canAct} />
            <iframe title={`${sop.title} (PDF)`} src={sopDownloadUrl(sop.id)} className="min-h-[70vh] w-full flex-1 bg-surface" />
          </div>
        ) : (
          <SopDocumentView sop={sop} template={template} showEmptySections={canAct} />
        )}
      </div>

      <SopEmailDrawer open={emailOpen} onOpenChange={setEmailOpen} sops={[{ id: sop.id, title: sop.title }]} />
    </div>
  )
}
